/**
 * Normalize Orchestrator
 *
 * Sequential DOM normalization: Schema → Clickable → Replace → Sync → Dupe.
 * Critical first phase that prepares DOM structure for all other modules.
 */
import config from '@config';
import { init as schemaInit } from './functions/schema.ts';
import { init as syncInit } from './functions/sync.ts';
import { init as clickableInit } from './functions/clickable.ts';
import { init as dupeInit } from './functions/dupe.ts';
import { init as replaceInit } from './functions/replace.ts';

const CONFIG_ROOT = 'normalize';

export async function init(): Promise<{ result: string; destroy: () => void }> {
  const cleanup: { destroyFunctions: (() => void)[] } = { destroyFunctions: [] };
  const moduleConfig = config[CONFIG_ROOT];

  try {
    // Phase 1a: Schema (generates JSON-LD structured data for SEO)
    const schemaResult = schemaInit();
    if (schemaResult && 'destroy' in schemaResult && typeof schemaResult.destroy === 'function') {
      cleanup.destroyFunctions.push(schemaResult.destroy as () => void);
    }

    // Phase 1b: Clickable (normalizes button/link structure)
    const clickableResult = await clickableInit();
    if (
      clickableResult &&
      'destroy' in clickableResult &&
      typeof clickableResult.destroy === 'function'
    ) {
      cleanup.destroyFunctions.push(clickableResult.destroy as () => void);
    }

    // Phase 1c: Replace (replaces {{placeholder}} tokens including year/month)
    const replaceResult = replaceInit(config.normalize?.replace);
    if (
      replaceResult &&
      'destroy' in replaceResult &&
      typeof replaceResult.destroy === 'function'
    ) {
      cleanup.destroyFunctions.push(replaceResult.destroy as () => void);
    }

    // Phase 1d: Sync (populates lists with collection data)
    const syncResult = await syncInit(
      moduleConfig.sync as unknown as {
        attributes: {
          elements: { item: { primary: string } };
          properties: { syncId: string; syncField: string; syncMode: string; syncValue: string };
        };
      }
    );
    if (syncResult && 'destroy' in syncResult && typeof syncResult.destroy === 'function') {
      cleanup.destroyFunctions.push(syncResult.destroy as () => void);
    }

    // Phase 1e: Dupe (duplicates normalized elements)
    const dupeResult = await dupeInit(
      moduleConfig.dupe as unknown as {
        attributes: { elements: { [key: string]: { primary: string; aliases?: string[] } } };
      }
    );
    if (dupeResult && 'destroy' in dupeResult && typeof dupeResult.destroy === 'function') {
      cleanup.destroyFunctions.push(dupeResult.destroy as () => void);
    }

    return {
      result: 'normalize initialized',
      destroy: () => {
        // Call all destroy functions in reverse order
        cleanup.destroyFunctions.reverse().forEach((destroyFn) => {
          try {
            destroyFn();
          } catch (error) {
            console.error('[normalize] Error during cleanup:', error);
          }
        });
        cleanup.destroyFunctions.length = 0;
      },
    };
  } catch (error) {
    console.error('[normalize] Initialization failed:', error);
    // Cleanup any partial initialization
    cleanup.destroyFunctions.reverse().forEach((fn) => {
      try {
        fn();
      } catch (cleanupError) {
        console.error('[normalize] Error during error cleanup:', cleanupError);
      }
    });
    throw error;
  }
}
